import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, DatePicker, Spin, Table, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { errorText } from "../api";
import { formatPeriod } from "../shared/money";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageToolbar from "../../../../design-system/PageToolbar";
import { lohnjournal } from "./api";
import { STEP_LABELS } from "./RunsList";

const Lohnjournal = () => {
  const { t } = useTranslation("accounting");
  const [period, setPeriod] = useState(dayjs().subtract(1, "month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lohnjournal(period.format("YYYY-MM"));
      setData(res.data);
    } catch (err) {
      message.error(errorText(err));
    }
    setLoading(false);
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const money = key => ({ dataIndex: key, align: "end", render: v => <Amount cents={v} /> });
  const columns = [
    {
      title: t("Employee"),
      dataIndex: ["employee", "name"],
      render: (v, r) => (
        <span>
          {v} {r.minijob ? <Tag color="purple">{t("Minijob")}</Tag> : null}{" "}
          {!r.locked ? <Tag color="orange">{t("preliminary")}</Tag> : null}
        </span>
      )
    },
    {
      title: t("Hours"),
      dataIndex: "hours",
      align: "end",
      render: v => <span className="acct-num">{v.toFixed(2)}</span>
    },
    { title: t("Gross"), ...money("gross") },
    { title: t("Lohnsteuer"), ...money("lohnsteuer") },
    { title: t("Soli"), ...money("solidaritaetszuschlag") },
    { title: t("KiSt"), ...money("kirchensteuer") },
    { title: t("SV employee"), ...money("employeeSv") },
    { title: t("Net"), ...money("net") },
    { title: t("Payout"), ...money("payout") },
    { title: t("SV employer"), ...money("employerSv") },
    { title: t("SOKA-BAU Umlage"), ...money("sokaBau") },
    { title: t("Employer cost"), ...money("employerCost") }
  ];
  const totalKeys = [
    "gross",
    "lohnsteuer",
    "solidaritaetszuschlag",
    "kirchensteuer",
    "employeeSv",
    "net",
    "payout",
    "employerSv",
    "sokaBau",
    "employerCost"
  ];

  return (
    <Spin spinning={loading}>
      <PageToolbar
        filters={
          <DatePicker
            picker="month"
            value={period}
            onChange={v => v && setPeriod(v)}
            allowClear={false}
            aria-label={t("Month")}
          />
        }
        actions={<Button onClick={() => window.print()}>{t("Print")}</Button>}
      />
      <Card
        size="small"
        title={
          <span className="acct-wrap" style={{ whiteSpace: "normal" }}>
            {`${t("Lohnjournal")} ${formatPeriod(period.format("YYYY-MM"))}${data?.run ? ` · ${t("Lohnlauf")}: ${STEP_LABELS[data.run.status] ? t(STEP_LABELS[data.run.status]) : data.run.status}` : ""}`}
          </span>
        }
      >
        <ResponsiveTable
          mobileLayout="table"
          className="acct-grid"
          rowKey="slip"
          size="small"
          pagination={false}
          dataSource={data?.rows || []}
          columns={columns}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>
                  {t("Total")} ({data?.totals?.employeeCount || 0})
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="end">
                <strong className="acct-num">{(data?.totals?.hours || 0).toFixed(2)}</strong>
              </Table.Summary.Cell>
              {totalKeys.map((key, i) => (
                <Table.Summary.Cell index={i + 2} key={key} align="end">
                  <Amount cents={data?.totals?.[key]} strong />
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          )}
        />
      </Card>
    </Spin>
  );
};

export default Lohnjournal;
